/**
 * Character Energy System — the "battery" layer that sits below the
 * operator-points wallet, one per (fan × character).
 *
 * Design (locked with the operator 2026-06):
 *   - 預設滿電 = 100 點 = 100%。商城「擴充電池」每次 +100 容量。
 *   - 回充 = 每小時「容量的 5%」(預設容量 100 → +5/時;擴充到 200 → +10/時)。
 *     懶算、無 cron、不回溯(滿了不囤,溢出丟棄)。
 *   - 消耗(底層皆「點」):文字 1、語音 3、開著陪伴 1/分。
 *     閒置 10 分鐘無對話 → 暫停(尾巴最多 10 點),有對話則重置。
 *   - 顯示 %=點/容量×100;≥50% 每 5% 一格、<50% 每 2% 一格(純視覺)。
 *   - 不足 → 擋下(不自動補錢包),由粉絲按「補點 / 商城」。
 *
 * Pure helpers live here (shared by backend + frontend); all DynamoDB
 * I/O lives in admin-api `lib/energy.ts`.
 */
import { z } from 'zod';
// ── Tunable constants (single source of truth) ─────────────────────
export const ENERGY_DEFAULT_CAPACITY = 100;
export const ENERGY_CAPACITY_STEP = 100; // 擴充電池每次 +100
export const ENERGY_REGEN_PCT_PER_HOUR = 5; // 回充 = 容量的 5%/小時
export const ENERGY_TEXT_COST = 1; // 文字訊息
export const ENERGY_VOICE_COST = 3; // 語音訊息
export const ENERGY_IDLE_COST_PER_MIN = 1; // 開著陪伴 1/分
export const ENERGY_IDLE_TIMEOUT_MIN = 10; // 連續無對話 N 分鐘後暫停
// 閒置尾巴上限 = 逾時分鐘 × 每分鐘成本(= 10 點),由上面兩者導出。
export const ENERGY_IDLE_MAX = ENERGY_IDLE_TIMEOUT_MIN * ENERGY_IDLE_COST_PER_MIN;
// 顯示刻度(純視覺)
export const ENERGY_NOTCH_THRESHOLD_PCT = 50;
export const ENERGY_NOTCH_HIGH_PCT = 5; // ≥50%
export const ENERGY_NOTCH_LOW_PCT = 2; // <50%
const MS_PER_HOUR = 3_600_000;
// ── Stored row (per fan × character) ───────────────────────────────
export const CharacterEnergySchema = z.object({
    fanId: z.string(),
    characterId: z.string(),
    /** Current energy in points (float — fractional regen accrues precisely). */
    energy: z.number().min(0),
    /** Max points (default 100; +100 per 擴充電池). */
    capacity: z.number().positive(),
    /** Regen as % of capacity per hour (default 5). Raised by 加速回充 items. */
    regenPct: z.number().min(0),
    /** ISO — last time energy was recomputed (regen anchor; never banks past full). */
    lastUpdateAt: z.string(),
    /** ISO — last chargeable activity (message / 點擊繼續); drives idle pause. */
    lastActivityAt: z.string().optional(),
    // Lifetime counters for the platform-subsidy report (no per-minute log rows).
    lifetimeRegen: z.number().default(0),
    lifetimeSpent: z.number().default(0),
    lifetimeSpentFromRegen: z.number().default(0),
    // Time-limited shop effects (optional).
    regenBoostUntil: z.string().optional(),
    passUntil: z.string().optional(),
});
// ── Pure helpers ───────────────────────────────────────────────────
/** Regen accrued lazily: energy + (capacity × regenPct/100) × hoursElapsed,
 *  clamped to capacity. Caller MUST set lastUpdateAt = now after, so the
 *  overflow while full is never banked (the 22:00→23:00 requirement). */
export function regenerate(energy, capacity, regenPct, lastUpdateAtMs, nowMs) {
    if (nowMs <= lastUpdateAtMs)
        return Math.min(capacity, Math.max(0, energy));
    const ratePerMs = (capacity * (regenPct / 100)) / MS_PER_HOUR;
    const gained = (nowMs - lastUpdateAtMs) * ratePerMs;
    return Math.min(capacity, energy + gained);
}
/** Snap a raw 0..100 % to the display notch (5% steps ≥50%, 2% steps <50%). */
export function snapDisplayPct(rawPct) {
    const step = rawPct >= ENERGY_NOTCH_THRESHOLD_PCT ? ENERGY_NOTCH_HIGH_PCT : ENERGY_NOTCH_LOW_PCT;
    return Math.max(0, Math.min(100, Math.round(rawPct / step) * step));
}
/** Per-message cost by mode. */
export function messageCost(mode) {
    return mode === 'voice' ? ENERGY_VOICE_COST : ENERGY_TEXT_COST;
}
/** Build the fan-facing view from a (regen-applied) row. */
export function toEnergyView(row, nowMs) {
    const rawPct = row.capacity > 0 ? (row.energy / row.capacity) * 100 : 0;
    return {
        energy: Math.round(row.energy),
        capacity: row.capacity,
        pct: snapDisplayPct(rawPct),
        rawPct: Math.max(0, Math.min(100, rawPct)),
        regenPerHour: Math.round(row.capacity * (row.regenPct / 100)),
        full: row.energy >= row.capacity,
        passActive: !!row.passUntil && Date.parse(row.passUntil) > nowMs,
    };
}
// ── Shop catalog ───────────────────────────────────────────────────
export const EnergyShopItemTypeSchema = z.enum([
    'recharge', // 充電包:立即 +amount(+bonus)電力
    'fill', // 立即充滿到 100%
    'expand', // 擴充電池:永久 +capacityDelta 容量
    'regen-boost', // 加速回充:regenPct → boostRegenPct(durationDays 為限時,缺省永久)
    'pass', // 暢聊月卡:durationDays 內免耗電
]);
export const EnergyShopItemSchema = z.object({
    id: z.string(),
    type: EnergyShopItemTypeSchema,
    name: z.string(),
    description: z.string().optional(),
    /** Price in operator points (經營者幣). */
    pricePoints: z.number().nonnegative(),
    enabled: z.boolean().default(true),
    sortOrder: z.number().default(0),
    // type-specific effect params
    amount: z.number().optional(), // recharge: base points
    bonus: z.number().optional(), // recharge: bonus points
    capacityDelta: z.number().optional(), // expand: +capacity (default ENERGY_CAPACITY_STEP)
    boostRegenPct: z.number().optional(), // regen-boost: new regenPct
    durationDays: z.number().optional(), // regen-boost / pass: duration
});
/** Platform default catalog. Overridable at runtime via the
 *  `energy-shop` row in nx-platform-settings (admin 角色電力 page).
 *  加速回充 / 暢聊月卡 ship disabled — the operator decided (2026-07-13)
 *  the launch shop is 充電-only; re-enable from the admin editor when
 *  those offers are ready to sell. */
export const DEFAULT_ENERGY_SHOP = [
    { id: 'recharge-50', type: 'recharge', name: '充電包 50', pricePoints: 50, amount: 50, bonus: 0, enabled: true, sortOrder: 1 },
    { id: 'recharge-100', type: 'recharge', name: '充電包 100（送 15）', pricePoints: 100, amount: 100, bonus: 15, enabled: true, sortOrder: 2 },
    { id: 'fill', type: 'fill', name: '立即充滿', pricePoints: 80, enabled: true, sortOrder: 3 },
    { id: 'expand-100', type: 'expand', name: '擴充電池 +100', pricePoints: 300, capacityDelta: 100, enabled: true, sortOrder: 4 },
    { id: 'boost-30d', type: 'regen-boost', name: '加速回充 30 天（8%/時）', pricePoints: 200, boostRegenPct: 8, durationDays: 30, enabled: false, sortOrder: 5 },
    { id: 'pass-30d', type: 'pass', name: '暢聊月卡 30 天', pricePoints: 600, durationDays: 30, enabled: false, sortOrder: 6 },
];
/** Admin PUT body for the energy-shop catalog override. */
export const EnergyShopCatalogSchema = z.array(EnergyShopItemSchema).max(20);
/** Body for PATCH energy admin-note? n/a. Body for shop purchase. */
export const EnergyPurchaseBodySchema = z.object({ itemId: z.string() });
